import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { fetchSharedCheck, trackEvent } from "../lib/api";
import { emitSharedPageViewed } from "../lib/rum";
import { cn } from "../lib/utils";
import ResultOgMeta from "../components/ResultOgMeta";

const VERDICT_COLORS = {
  SAFE_TOGETHER: "bg-emerald-50 text-emerald-700 border-emerald-200",
  USE_WITH_CAUTION: "bg-amber-50 text-amber-800 border-amber-200",
  AVOID: "bg-rose-50 text-rose-700 border-rose-200",
  INSUFFICIENT_DATA: "bg-cream-100 text-ink-soft border-surface-line",
  // Legacy verdict codes.
  SAFE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  MOSTLY_SAFE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CAUTION: "bg-amber-50 text-amber-800 border-amber-200",
};
const VERDICT_LABEL = {
  SAFE_TOGETHER: "No label conflict found",
  USE_WITH_CAUTION: "Use with caution",
  AVOID: "Avoid",
  INSUFFICIENT_DATA: "Not enough verified data",
  // Legacy verdict codes.
  SAFE: "No label conflict found", MOSTLY_SAFE: "No label conflict found",
  CAUTION: "Use with caution",
};

export default function SharedResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSharedCheck(id)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        // RUM: fire-and-forget share-page-viewed signal so the funnel
        // (shared_page_viewed → shared_rerun_clicked → check_completion)
        // can be measured in the admin dashboard.
        emitSharedPageViewed(id, { verdict: d?.verdict });
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? "This share link has expired or never existed."
              : "Could not load shared verdict.",
          );
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Sticky-bar deep-link: re-run this exact combo back in the checker
  // with both fields pre-filled and the verdict auto-running. Memoised
  // BEFORE the early returns so React's rules-of-hooks stay green.
  const rerunHref = useMemo(() => {
    const params = new URLSearchParams();
    const medSlug =
      (data?.medication_slugs && data.medication_slugs[0]) ||
      (data?.medication_ids && data.medication_ids[0]) ||
      "";
    const prodSlug = data?.product_slug || data?.product_id || "";
    if (medSlug) params.set("med", medSlug);
    if (prodSlug) params.set("product", prodSlug);
    // RUM attribution: tag the re-run check so we can compute the
    // share→completion conversion rate in /admin/events.
    if (id) params.set("from_share_id", id);
    const qs = params.toString();
    return qs ? `/check?${qs}` : "/check";
  }, [data, id]);

  if (loading) {
    return (
      <main className="min-h-screen pt-28 pb-16 px-4 flex items-center justify-center bg-cream-50"
            data-testid="shared-result-loading">
        <p className="text-ink-mute animate-pulse">Loading shared verdict…</p>
      </main>
    );
  }
  if (error) {
    return (
      <main className="min-h-screen pt-28 pb-16 px-4 flex items-center justify-center bg-cream-50"
            data-testid="shared-result-error">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-bold text-ink">Verdict not found</h1>
          <p className="text-ink-mute">{error}</p>
          <Button onClick={() => navigate("/check")} data-testid="shared-result-cta">
            Run your own check <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    );
  }

  const v = data?.verdict || "INSUFFICIENT_DATA";
  const tone = VERDICT_COLORS[v] || VERDICT_COLORS.INSUFFICIENT_DATA;
  const when = data?.checked_at ? new Date(data.checked_at).toLocaleString() : null;

  // Show the sticky bar only when we have at least one side of the combo
  // — without anything pre-fillable, /check is the cleaner CTA.
  const hasDeepLink = rerunHref.includes("med=") || rerunHref.includes("product=");

  return (
    <main className="min-h-screen pt-28 pb-40 px-4 bg-cream-50" data-testid="shared-result-page">
      <ResultOgMeta historyId={id} />
      <div className="max-w-xl mx-auto">
        <p className="text-xs uppercase tracking-[0.22em] font-bold text-peach-700 mb-3">
          Shared verdict · Blendsafe
        </p>
        <div className={cn("rounded-3xl border p-8", tone)}>
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-80">Verdict</p>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="text-4xl sm:text-5xl font-extrabold">
              {VERDICT_LABEL[v] || v}
            </h1>
            {typeof data?.safety_score === "number" && (
              <span className="text-xl font-semibold opacity-80" data-testid="shared-score">
                {data.safety_score}/100
              </span>
            )}
          </div>

          <div className="mt-6 space-y-2">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-70">
              Medication{data?.medication_names?.length > 1 ? "s" : ""}
            </p>
            <p className="text-lg font-bold" data-testid="shared-medications">
              {(data?.medication_names || []).join(" + ") || "—"}
            </p>
          </div>

          <div className="mt-4 space-y-2">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-70">
              Product
            </p>
            <p className="text-lg font-bold" data-testid="shared-product">
              {data?.product_name || "—"}
            </p>
          </div>

          {data?.top_issues?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-70 mb-1.5">
                Top issues
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="shared-top-issues">
                {data.top_issues.map((iss) => (
                  <span key={iss} className="px-2.5 py-1 rounded-full bg-white/70 text-xs font-semibold border border-white/60">
                    {iss}
                  </span>
                ))}
              </div>
            </div>
          )}
          {when && (
            <p className="mt-6 text-xs opacity-60" data-testid="shared-timestamp">
              Checked {when}
            </p>
          )}
        </div>

        <div className="mt-6 rounded-3xl bg-white border border-surface-line p-6 text-center">
          <Sparkles className="w-5 h-5 mx-auto text-peach-500 mb-2" />
          <p className="text-ink font-semibold">Run your own check on Blendsafe</p>
          <p className="text-sm text-ink-mute mt-1">
            See how your medications react with any skincare product — free for 3 checks/month.
          </p>
          <Button
            onClick={() => navigate("/check")}
            data-testid="shared-result-cta"
            className="mt-4"
          >
            Check my skincare <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Sticky "Re-run this check" bar — appears only when we can build a
          deep-link (i.e. the shared verdict has at least a med-id or
          product-id we can pre-fill in the checker). Sits above the
          mobile keyboard / footer with a sand-cream/border combo that
          matches the result card. Backdrop-blur so it stays legible
          even when the card scrolls underneath. */}
      {hasDeepLink && (
        <div
          data-testid="shared-rerun-bar"
          className="fixed inset-x-0 bottom-0 z-30 border-t border-surface-line bg-cream-50/95 backdrop-blur supports-[backdrop-filter]:bg-cream-50/80"
        >
          <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3 sm:py-3.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold uppercase tracking-[0.18em] text-peach-700">
                Same combo, my products
              </p>
              <p className="mt-0.5 truncate text-sm text-ink">
                <span className="font-semibold">
                  {(data?.medication_names || []).join(" + ") || "your medication"}
                </span>
                {data?.product_name ? (
                  <>
                    {" "}
                    <span className="text-ink-mute">×</span>{" "}
                    <span className="font-semibold">{data.product_name}</span>
                  </>
                ) : null}
              </p>
            </div>
            <Button
              onClick={() => {
                trackEvent("shared_rerun_clicked", {
                  share_id: id,
                  has_med: !!data?.medication_ids?.[0],
                  has_product: !!data?.product_id,
                  verdict: data?.verdict,
                });
                navigate(rerunHref);
              }}
              data-testid="shared-rerun-cta"
              size="sm"
              className="shrink-0"
            >
              <RotateCcw className="mr-1.5 h-4 w-4" />
              Re-run with my products
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}
